import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { detectPlatform } from "@/lib/shareHandler";
import { invokeGenerator } from "@/lib/invokeGenerator";


// URL-PASTE-SOCIAL-1: platforms with no orchestrator support yet.
// Everything else that detectPlatform() recognises (Instagram, TikTok,
// Pinterest, Facebook) is routed to analyse-outfit-social.
const UNSUPPORTED_HOSTS: Array<{
  host: string;
  platform: "twitter" | "x" | "threads";
}> = [
  { host: "twitter.com", platform: "twitter" },
  { host: "x.com", platform: "x" },
  { host: "threads.net", platform: "threads" },
];

function unsupportedSocialPlatform(
  hostname: string,
): "twitter" | "x" | "threads" | null {
  const lc = hostname.toLowerCase();
  for (const { host, platform } of UNSUPPORTED_HOSTS) {
    if (lc === host || lc.endsWith("." + host)) return platform;
  }
  return null;
}

function classifyUrlShape(url: string): string {
  try {
    const u = new URL(url);
    const seg = u.pathname.split("/").filter(Boolean)[0] ?? "root";
    return seg.toLowerCase();
  } catch {
    return "unknown";
  }
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  guestSessionId: string | null;
  /** Called once outfit row is pre-inserted; parent navigates to results. */
  onSubmitted: (outfitId: string, hostname: string) => void;
  /**
   * PQ-3b: guest gate. Return false to block submission (paywall opens via
   * parent side effect). Default: always allowed.
   */
  canSubmit?: () => boolean;
}

type ErrorKey =
  | null
  | "unsupported_social"
  | "invalid"
  | "network"
  | "no_garment"
  | "insert_failed";

const ERROR_COPY: Record<Exclude<ErrorKey, null>, string> = {
  unsupported_social:
    "Twitter/X and Threads aren't supported yet. Try Instagram, TikTok, Pinterest, or Facebook — or a retailer product page.",
  invalid: "That doesn't look like a URL. Try pasting again.",
  network: "We couldn't read this URL. Try a retailer product page link.",
  no_garment: "We couldn't find an outfit on this page. Try a product page.",
  insert_failed: "Something went wrong. Try again.",
};

function parseUrl(value: string): URL | null {
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  try {
    return new URL(trimmed);
  } catch {
    return null;
  }
}

export default function UrlPasteSheet({
  open,
  onOpenChange,
  userId,
  guestSessionId,
  onSubmitted,
  canSubmit,
}: Props) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<ErrorKey>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setValue("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  // Real-time unsupported-platform detection (twitter/x/threads).
  // Instagram/TikTok/Pinterest/Facebook are supported — no inline warning.
  useEffect(() => {
    if (!value.trim()) {
      setError(null);
      return;
    }
    const parsed = parseUrl(value);
    if (parsed && unsupportedSocialPlatform(parsed.hostname)) {
      setError("unsupported_social");
    } else if (error === "unsupported_social") {
      setError(null);
    }
  }, [value, error]);

  const parsed = useMemo(() => parseUrl(value), [value]);
  const ctaEnabled =
    !submitting &&
    !!parsed &&
    !unsupportedSocialPlatform(parsed.hostname);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setValue(text.trim());
      } else {
        inputRef.current?.focus();
      }
    } catch {
      inputRef.current?.focus();
    }
  };

  const handleSubmit = async () => {
    if (!ctaEnabled || !parsed) return;
    const url = value.trim();
    const hostname = parsed.hostname;

    // Reject twitter/x/threads with targeted copy + demand-signal analytics.
    const unsupported = unsupportedSocialPlatform(hostname);
    if (unsupported) {
      trackEvent("unsupported_social_pasted", {
        platform: unsupported,
        url_shape: classifyUrlShape(url),
      });
      setError("unsupported_social");
      return;
    }

    // PQ-3b: guest paywall gate fires BEFORE outfit insert + edge fn.
    if (canSubmit && !canSubmit()) {
      onOpenChange(false);
      return;
    }

    // URL-PASTE-SOCIAL-1: route recognised social URLs through the SAME
    // orchestrator the share-intent path uses (analyse-outfit-social),
    // mirroring src/lib/shareHandler.ts handleSharedUrl social branch.
    const socialPlatform = detectPlatform(url);
    if (socialPlatform) {
      void triggerHaptic("success");
      setSubmitting(true);
      setError(null);

      const outfitId = crypto.randomUUID();
      const { error: insertError } = await supabase.from("outfits").insert({
        id: outfitId,
        user_id: userId ?? null,
        is_guest: !userId,
        guest_session_id: !userId ? guestSessionId : null,
        image_url: null,
        lookbook_status: "pending",
        input_method: "url",
        source_url: url,
        source_type: "social",
        source_platform: socialPlatform,
      } as never);

      if (insertError) {
        console.error("[URL-PASTE-SOCIAL-1] outfits insert failed", insertError, {
          userId,
          guestSessionId,
          url,
          socialPlatform,
        });
        setSubmitting(false);
        toast.error(insertError.message ?? "Couldn't submit URL");
        setError("insert_failed");
        return;
      }

      trackEvent("social_url_pasted", {
        platform: socialPlatform,
        url_shape: classifyUrlShape(url),
      });

      // Load budget/gender defaults for orchestrator (mirrors share-intent).
      let userBudget: number | null = null;
      let budgetTop: number | null = null;
      let budgetBottom: number | null = null;
      let budgetShoes: number | null = null;
      let gender: "women" | "men" | undefined = undefined;
      if (userId) {
        const { data: profile } = await supabase
          .from("profiles")
          .select(
            "budget_top, budget_bottom, budget_shoes, user_budget, size_gender, gender",
          )
          .eq("id", userId)
          .maybeSingle();
        const p = profile as
          | {
              budget_top?: number | null;
              budget_bottom?: number | null;
              budget_shoes?: number | null;
              user_budget?: number | null;
              size_gender?: string | null;
              gender?: string | null;
            }
          | null;
        budgetTop = p?.budget_top ?? null;
        budgetBottom = p?.budget_bottom ?? null;
        budgetShoes = p?.budget_shoes ?? null;
        userBudget = p?.user_budget ?? null;
        const rawGender = p?.size_gender ?? p?.gender ?? null;
        if (rawGender === "woman" || rawGender === "women") gender = "women";
        else if (rawGender === "man" || rawGender === "men") gender = "men";
      } else if (typeof window !== "undefined") {
        const g = window.localStorage.getItem("guest_gender");
        if (g === "women" || g === "men") gender = g;
      }

      void invokeGenerator("analyse-outfit-social", {
        source: "url-paste-social",
        body: {
          outfit_id: outfitId,
          source_platform: socialPlatform,
          source_url: url,
          user_id: userId ?? null,
          is_guest: !userId,
          guest_session_id: !userId ? guestSessionId : null,
          gender,
          user_budget: userBudget,
          budget_top: budgetTop,
          budget_bottom: budgetBottom,
          budget_shoes: budgetShoes,
        },
      })
        .then(async ({ data, error: invokeError, quotaExceeded }) => {
          if (quotaExceeded) return;
          if (invokeError || (data && (data as { success?: boolean }).success === false)) {
            console.error("[URL-PASTE-SOCIAL-1] analyse-outfit-social failed", {
              invokeError,
              data,
              socialPlatform,
            });
            toast.error(
              invokeError?.message ??
                "We couldn't analyse that link. Try a different post.",
            );
            trackEvent("social_url_analysis_failed", {
              platform: socialPlatform,
              reason:
                (data && ((data as { error?: string }).error as string)) ||
                invokeError?.message ||
                "unknown",
            });
            await supabase
              .from("outfits")
              .update({ lookbook_status: "failed" } as never)
              .eq("id", outfitId);
          }
        });


      onSubmitted(outfitId, hostname);
      return;
    }

    void triggerHaptic("success");
    setSubmitting(true);
    setError(null);

    const outfitId = crypto.randomUUID();
    const { error: insertError } = await supabase.from("outfits").insert({
      id: outfitId,
      user_id: userId ?? null,
      is_guest: !userId,
      guest_session_id: !userId ? guestSessionId : null,
      // image_url is nullable; wrapper edge fn fills it once og:image is downloaded.
      image_url: null,
      lookbook_status: "pending",
      input_method: "url",
      source_url: url,
      source_type: "url",
    } as never);

    if (insertError) {
      console.error("[F37.2] outfits insert failed", insertError, {
        userId,
        guestSessionId,
        url,
      });
      setSubmitting(false);
      toast.error(insertError.message ?? "Couldn't submit URL");
      setError("insert_failed");
      return;
    }

    trackEvent("url_submitted", {
      domain: hostname,
      is_retailer: true,
    });

    // Forward authed user's budget profile so URL flow tier filtering
    // anchors budget/premium picks around the user's stored thresholds.
    let userBudget: number | null = null;
    let budgetTop: number | null = null;
    let budgetBottom: number | null = null;
    let budgetShoes: number | null = null;
    if (userId) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("budget_top, budget_bottom, budget_shoes, user_budget")
        .eq("id", userId)
        .maybeSingle();
      budgetTop = (profile as { budget_top?: number | null } | null)?.budget_top ?? null;
      budgetBottom = (profile as { budget_bottom?: number | null } | null)?.budget_bottom ?? null;
      budgetShoes = (profile as { budget_shoes?: number | null } | null)?.budget_shoes ?? null;
      userBudget = (profile as { user_budget?: number | null } | null)?.user_budget ?? null;
    }

    // Fire-and-forget the edge fn; results page renders progressively.
    void invokeGenerator("analyse-outfit-url", {
      source: "url-paste",
      body: {
        outfit_id: outfitId,
        url,
        user_id: userId ?? null,
        guest_session_id: !userId ? guestSessionId : null,
        user_budget: userBudget,
        budget_top: budgetTop,
        budget_bottom: budgetBottom,
        budget_shoes: budgetShoes,
      },
    })
      .then(async ({ data, error: invokeError, quotaExceeded }) => {
        if (quotaExceeded) return;
        if (invokeError || (data && (data as { success?: boolean }).success === false)) {
          console.error("[F37.2] analyse-outfit-url failed", {
            invokeError,
            data,
          });
          toast.error(
            invokeError?.message ??
              "We couldn't analyse that URL. Try again or upload a screenshot.",
          );
          trackEvent("url_analysis_failed", {
            domain: hostname,
            reason:
              (data && ((data as { error?: string }).error as string)) ||
              invokeError?.message ||
              "unknown",
          });
          await supabase
            .from("outfits")
            .update({ lookbook_status: "failed" } as never)
            .eq("id", outfitId);
        }
      });


    onSubmitted(outfitId, hostname);
  };

  const inputBorder = error
    ? "1.5px solid #BA7517"
    : "1px solid #E8E2D9";

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="fixed inset-0 z-50"
          style={{ background: "rgba(44,40,32,0.4)" }}
        />
        <DrawerPrimitive.Content
          className="fixed inset-x-0 z-50 flex flex-col"
          style={{
            bottom: "calc(78px + var(--safe-bottom))",
            background: "#FFFCF8",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderBottomLeftRadius: 20,
            borderBottomRightRadius: 20,
            marginLeft: 8,
            marginRight: 8,
            outline: "none",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 36,
              height: 4,
              borderRadius: 999,
              background: "#E0D8D0",
              margin: "8px auto",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              padding: "0 16px 4px",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="l2f-type-section-header"
                style={{ color: "#C84B2F" }}
              >
                PASTE A URL
              </div>
              <DrawerPrimitive.Title
                style={{
                  fontSize: 20,
                  fontStyle: "italic",
                  fontFamily:
                    "var(--font-serif), 'Instrument Serif', Georgia, serif",
                  color: "#2C2820",
                  lineHeight: 1.1,
                  marginTop: 2,
                  fontWeight: 400,
                }}
              >
                Paste a product URL
              </DrawerPrimitive.Title>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => onOpenChange(false)}
              style={{
                width: 44,
                height: 44,
                background: "transparent",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                padding: 0,
                margin: "-6px -6px 0 0",
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: "#F1EFE8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="ti ti-x"
                  style={{ fontSize: 14, color: "#4A4540" }}
                />
              </span>
            </button>
          </div>
          <DrawerPrimitive.Description
            className="l2f-type-caption"
            style={{
              padding: "4px 16px 14px",
              color: "#6B6560",
            }}
          >
            From a retailer product page or a social post link.
          </DrawerPrimitive.Description>

          <div style={{ padding: "0 16px 6px" }}>
            <div
              className="l2f-type-section-header"
              style={{
                color: "#9B958E",
                marginBottom: 6,
              }}
            >
              PRODUCT URL
            </div>
            <div
              style={{
                width: "100%",
                minHeight: 48,
                background: "#FFFCF8",
                border: inputBorder,
                borderRadius: 12,
                padding: "0 14px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <input
                ref={inputRef}
                type="text"
                inputMode="url"
                autoComplete="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="https://..."
                style={{
                  flex: 1,
                  fontSize: 16,
                  color: "#2C2820",
                  padding: "12px 0",
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  wordBreak: "break-all",
                  minWidth: 0,
                }}
              />
              {value.length === 0 ? (
                <button
                  type="button"
                  onClick={handlePaste}
                  style={{
                    minHeight: 44,
                    minWidth: 44,
                    background: "transparent",
                    border: "none",
                    fontSize: 12,
                    color: "#C84B2F",
                    fontWeight: 500,
                    padding: "4px 8px",
                    cursor: "pointer",
                  }}
                >
                  Paste
                </button>
              ) : (
                <button
                  type="button"
                  aria-label="Clear"
                  onClick={() => setValue("")}
                  style={{
                    width: 44,
                    height: 44,
                    margin: "0 -11px",
                    background: "transparent",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <i
                    className="ti ti-x"
                    style={{ fontSize: 14, color: "#9B958E" }}
                  />
                </button>
              )}
            </div>
            {error && (
              <div
                style={{
                  paddingTop: 6,
                  fontSize: 11,
                  color: "#BA7517",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <i
                  className="ti ti-alert-circle"
                  style={{ fontSize: 12 }}
                />
                <span>{ERROR_COPY[error]}</span>
              </div>
            )}
          </div>

          <div
            style={{
              padding: "8px 16px 14px",
              fontSize: 11,
              color: "#9B958E",
              textAlign: "center",
            }}
          >
            Retailers (ASOS, Zara, H&amp;M, Reiss…) plus Instagram, TikTok, Pinterest &amp; Facebook
          </div>

          <div style={{ padding: "0 16px 16px" }}>
            <button
              type="button"
              disabled={!ctaEnabled}
              onClick={() => void handleSubmit()}
              style={{
                width: "100%",
                padding: 12,
                minHeight: 44,
                borderRadius: 22,
                fontSize: 14,
                fontWeight: 500,
                border: "none",
                cursor: ctaEnabled ? "pointer" : "not-allowed",
                background: ctaEnabled ? "#C84B2F" : "#D3D1C7",
                color: ctaEnabled ? "#fff" : "#9B958E",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {submitting ? (
                <>
                  <i
                    className="ti ti-loader"
                    style={{
                      fontSize: 14,
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  Fetching...
                </>
              ) : (
                "Find this look"
              )}
            </button>
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}
